/**
 * useWorkspace - 워크스페이스 컨텍스트 Hook
 *
 * ISSUE_182: 워크스페이스 = 학원 단일. scratch(연습장) 폐기.
 * 모든 데이터 쿼리와 설정 컴포넌트에서 이 Hook을 사용하세요.
 *
 * @example
 * ```tsx
 * const { workspaceFilter, isAcademyWorkspace } = useWorkspace();
 *
 * // Supabase 쿼리에 사용
 * const query = supabase.from("students").select("*");
 * if (workspaceFilter.academyId) {
 *   query.eq("academy_id", workspaceFilter.academyId);
 * }
 * ```
 *
 * @module core/hooks/useWorkspace
 */

import { useAuthStore } from "~/core/store/useAuthStore";
import { useWorkspaceStore } from "~/core/store/useWorkspaceStore";

// ============================================================================
// TYPES
// ============================================================================

export interface WorkspaceFilter {
  /** 학원 ID (미선택이면 null) */
  academyId: string | null;
}

export interface WorkspaceContext {
  /** 학원 워크스페이스 여부 (학원 선택됨) */
  isAcademyWorkspace: boolean;
  /** Supabase 쿼리용 필터 */
  workspaceFilter: WorkspaceFilter;
  /** 학원 ID (미선택이면 null) */
  academyId: string | null;
  /** 학원 이름 (미선택이면 null) */
  academyName: string | null;
  /** 소유자/사용자 ID */
  ownerId: string | null;
  /** 워크스페이스가 유효한지 (학원이 선택됐는지) */
  hasValidWorkspace: boolean;
  /** 현재 역할 */
  role: string | null;
  /** 커스텀 역할 라벨 */
  roleLabels: Record<string, string> | null;
  /** 인증/워크스페이스 초기화 중인지 (true면 로딩 UI 표시 필요) */
  isInitializing: boolean;
}

// ============================================================================
// HOOK
// ============================================================================

/**
 * 현재 워크스페이스 컨텍스트를 반환합니다.
 *
 * 워크스페이스 상태는 useWorkspaceStore에서 가져옵니다.
 * (profile.academyId는 deprecated - 워크스페이스 스위칭을 반영하지 않음)
 *
 * @returns WorkspaceContext
 */
export function useWorkspace(): WorkspaceContext {
  const { profile, isInitialized, isProfileLoading } = useAuthStore();
  const activeWorkspace = useWorkspaceStore((state) => state.active);
  const isWorkspaceLoading = useWorkspaceStore((state) => state.isLoading);
  const lastWorkspaceSync = useWorkspaceStore((state) => state.lastSync);

  const ownerId = profile?.id ?? null;

  // 인증 또는 워크스페이스 초기화 중인지 확인
  // Safety: 프로필이 있고 워크스페이스가 한번이라도 로드됐으면 초기화 완료로 간주
  // (오래된 세션에서 로딩 상태가 멈추는 edge case 방지)
  const hasLoadedOnce = !!profile && !!lastWorkspaceSync;
  const isInitializing = !hasLoadedOnce && (!isInitialized || isProfileLoading || isWorkspaceLoading);

  // ISSUE_182: 워크스페이스 = 학원 단일. active.id==='' 이면 미선택(런처가 게이트).
  const academyId = activeWorkspace.id || null;
  const academyName = activeWorkspace.name || null;
  const role = activeWorkspace.role;
  const roleLabels = activeWorkspace.roleLabels ?? null;

  const isAcademyWorkspace = !!academyId;
  const hasValidWorkspace = !!academyId;

  // Supabase 쿼리용 필터
  const workspaceFilter: WorkspaceFilter = {
    academyId,
  };

  return {
    isAcademyWorkspace,
    workspaceFilter,
    academyId,
    academyName,
    ownerId,
    hasValidWorkspace,
    isInitializing,
    role,
    roleLabels,
  };
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * 워크스페이스 필터를 Supabase insert 필드로 변환합니다.
 *
 * @example
 * ```ts
 * const insertData = {
 *   name: "홍길동",
 *   ...getWorkspaceInsertFields(workspaceFilter),
 * };
 * // 결과: { name: "홍길동", academy_id: "xxx" }
 * ```
 */
export function getWorkspaceInsertFields(
  filter: WorkspaceFilter
): { academy_id: string } | Record<string, never> {
  if (filter.academyId) {
    return { academy_id: filter.academyId };
  }
  return {};
}

/**
 * Supabase 쿼리에 워크스페이스 필터를 적용합니다.
 *
 * @example
 * ```ts
 * let query = supabase.from("students").select("*");
 * query = applyWorkspaceFilter(query, workspaceFilter);
 * ```
 */
export function applyWorkspaceFilter<T extends { eq: (column: string, value: string) => T }>(
  query: T,
  filter: WorkspaceFilter
): T {
  if (filter.academyId) {
    return query.eq("academy_id", filter.academyId);
  }
  return query;
}

export default useWorkspace;
